import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { APPLY_VIDEOS } from "../mock/mock";

const YouTubeEmbed = ({ id, title }) => {
  const [play, setPlay] = useState(false);
  return (
    <div className="relative aspect-video bg-black overflow-hidden" data-testid={`video-${id}`}>
      {play ? (
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlay(true)}
          data-testid={`video-play-${id}`}
          className="group absolute inset-0 w-full h-full"
          aria-label={`Play: ${title}`}
        >
          <img
            src={`https://i.ytimg.com/vi/${id}/maxresdefault.jpg`}
            onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`; }}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <span className="absolute inset-0 bg-black/30 group-hover:bg-black/45 transition-colors" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#a67c52]/90 group-hover:bg-[#a67c52] group-hover:scale-105 flex items-center justify-center transition-all">
              <Play size={30} className="text-white ml-1" fill="currentColor" />
            </span>
          </span>
        </button>
      )}
    </div>
  );
};

export const ApplyVideos = ({ compact = false }) => {
  const { lang, ui } = useLang();
  const ordered = [...APPLY_VIDEOS].sort((a, b) => (a.code === lang ? -1 : b.code === lang ? 1 : 0));

  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-12">
      {ordered.map((v) => (
        <div key={v.id} data-testid={`apply-card-${v.code}`}>
          <YouTubeEmbed id={v.id} title={v.title} />
          <div className="flex items-center gap-3 mt-5">
            <span className="text-[10px] uppercase tracking-[0.18em] bg-[#a67c52] text-white px-2 py-[3px]">{v.langLabel}</span>
            <h3 className="font-serif text-xl md:text-2xl text-[#211d18]">{v.title}</h3>
          </div>

          {compact ? (
            <>
              {v.intro && <p className="text-[#5a5348] text-[14.5px] leading-relaxed mt-3 line-clamp-2">{v.intro}</p>}
              <Link
                to="/nasil-uygulanir"
                data-testid={`apply-more-${v.code}`}
                className="group mt-4 flex items-center justify-between gap-4 border border-[#a67c52]/60 hover:border-[#a67c52] hover:bg-[#a67c52] px-5 py-3.5 transition-colors"
              >
                <span className="text-[12px] uppercase tracking-[0.14em] text-[#211d18] group-hover:text-white transition-colors">{ui.applyFull}</span>
                <ArrowRight size={16} className="text-[#a67c52] group-hover:text-white group-hover:translate-x-1 transition-all" />
              </Link>
            </>
          ) : (
            <>
              {v.intro && <p className="text-[#5a5348] text-[14.5px] leading-relaxed mt-3">{v.intro}</p>}
              <ol className="mt-4 space-y-2.5">
                {v.steps.map((s, i) => (
                  <li key={i} className="flex gap-3 text-[14px] leading-relaxed text-[#4a443c]">
                    <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full border border-[#a67c52]/50 text-[#a67c52] text-[12px] font-medium">{i + 1}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-5 border-l-2 border-[#a67c52] bg-[#f3ece1] px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.14em] text-[#a67c52] mb-1">{ui.applyNeedHelp}</p>
                <p className="text-[13px] leading-relaxed text-[#4a443c]">{v.help}</p>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default ApplyVideos;
